import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { IAssistantThread } from './assistant.interfaces';
import { ChatStateService } from '../chat-state.service';
import { OpenaiAPIService } from '../../api/openai-api.service';
import { ConversationsApiService } from '../../api/conversations-api.service';

/**
 * AssistantThread - Handles assistant thread management
 *
 * This service will handle:
 * - Thread creation
 * - Thread existence checks
 * - Thread state management
 * - Thread deletion
 */
@Injectable({
  providedIn: 'root',
})
export class AssistantThread implements IAssistantThread {
  constructor(
    private stateService: ChatStateService,
    private openAIService: OpenaiAPIService,
    private conversationsService: ConversationsApiService
  ) {}

  /**
   * Create a new thread for assistant
   */
  createThread(assistantId: string): Observable<string> {
    // Create a new thread and return its ID
    return this.openAIService.createThread().pipe(
      map(thread => thread.id)
    );
  }

  /**
   * Creates or retrieves a thread for assistant conversations
   * Preserved exactly from ChatApiService
   */
  createAssistantThread(): Observable<string> {
    return this.openAIService.createThread().pipe(
      tap((thread) => {
        // Update state with thread ID using simplified API
        this.stateService.threadId = thread.id;
      }),
      switchMap((thread) => {
        // Update conversation with thread ID if exists
        if (this.stateService.conversation.conversation_id) {
          return this.conversationsService.updateConversationThread(
            this.stateService.conversation.conversation_id,
            thread.id
          ).pipe(map(() => thread.id));
        }
        return of(thread.id);
      })
    );
  }

  /**
   * Ensure thread exists for assistant
   */
  ensureThreadExists(assistantId: string): Observable<string>;
  ensureThreadExists(): Observable<string>;
  ensureThreadExists(assistantId?: string): Observable<string> {
    // When no assistantId provided, use the legacy behavior from ChatApiService
    if (assistantId === undefined) {
      if (!this.stateService.threadId) {
        return this.createAssistantThread();
      }
      return of(this.stateService.threadId);
    }
    
    // For specific assistant, check if we have a thread ID in state
    // If not, create a new thread
    const currentThreadId = this.stateService.threadId;
    if (!currentThreadId) {
      return this.createThread(assistantId);
    }
    return of(currentThreadId);
  }

  /**
   * Get thread ID for assistant
   */
  getThreadId(assistantId: string): string | undefined {
    // For now, return the current thread ID from state
    // In the future, this could maintain a map of assistant IDs to thread IDs
    return this.stateService.threadId;
  }

  /**
   * Delete thread
   */
  deleteThread(threadId: string): Observable<void> {
    // OpenAI doesn't provide a delete thread endpoint in the current implementation
    // So we just clear it from state
    if (this.stateService.threadId === threadId) {
      this.stateService.threadId = '';
    }
    return of(undefined);
  }
}
